import { ApolloQueryResult } from 'apollo-client';

import { CampaignIdAndMetrikaCountersInput } from '../../actions/campaignsEdit';
import { BaseDataProvider } from '../../api/BaseDataProvider/BaseDataProvider';
import {
    broadmatchGoalsQuery,
    campaignsABSegmentsQuery,
    campaignsABSegmentsQueryForNewCampaign,
    campaignsAvailableForKeyGoalsForNewCampaignQuery,
    campaignsAvailableForKeyGoalsQuery,
    campaignsEditDataQuery,
    defaultCampaignsEditQuery
} from '../../graphql/queries/grid/campaignsEdit';
import { timeZoneGroupsQuery } from '../../graphql/queries/grid/common';
import { retargetingConditionsQueryForCampaignsEdit } from '../../graphql/queries/grid/retargeting';
import { CampaignType } from '../../typings/domain';
import {
    BroadmatchGoalsQuery,
    CampaignsABSegmentsQuery,
    CampaignsABSegmentsQueryForNewCampaign,
    CampaignsAvailableForKeyGoalsForNewCampaignQuery,
    CampaignsAvailableForKeyGoalsQuery,
    CampaignsEditDataQuery,
    DefaultCampaignsEditQuery,
    GdCampaignOrderByFieldInput,
    GdRetargetingConditionTypeInput,
    OrderInput,
    RetargetingConditionsQueryForCampaignsEdit,
    TimeZoneGroupsQuery
} from '../../typings/graphql-grid';

export class CampaignsDataProvider extends BaseDataProvider {
    public getCampaigns = (
        login: string,
        campaignsIds: Array<string>
    ): Promise<ApolloQueryResult<CampaignsEditDataQuery>> => {
        return this.createQuery<CampaignsEditDataQuery>(campaignsEditDataQuery, {
            variables: {
                login: login,
                campaignInput: {
                    filter: {
                        campaignIdIn: campaignsIds
                    },
                    orderBy: {
                        field: GdCampaignOrderByFieldInput.ID,
                        order: OrderInput.ASC
                    },
                    statRequirements: { preset: 'TODAY' },
                    limitOffset: {
                        offset: 0,
                        limit: 1
                    }
                }
            }
        });
    };

    public getBroadMatchGoals = (
        login: string,
        campaignsIds: Array<string>
    ): Promise<ApolloQueryResult<BroadmatchGoalsQuery>> => {
        return this.createQuery<BroadmatchGoalsQuery>(broadmatchGoalsQuery, {
            variables: {
                login: login,
                broadMatchGoalsInput: {
                    broadMatchContainers: campaignsIds.map(campaignId => {
                        return {
                            campaignId
                        };
                    })
                }
            }
        });
    };

    public getRetargetingConditions = (
        login: string
    ): Promise<ApolloQueryResult<RetargetingConditionsQueryForCampaignsEdit>> => {
        const query = this.createQuery<RetargetingConditionsQueryForCampaignsEdit>(
            retargetingConditionsQueryForCampaignsEdit,
            {
                variables: {
                    retargetingConditionsInput: {
                        filter: {
                            retargetingConditionTypeIn: [GdRetargetingConditionTypeInput.METRIKA_GOALS],
                            interest: false
                        }
                    },
                    login
                }
            }
        );

        return query;
    };

    public getDefaultCampaignsEditQuery = (
        login: string,
        campaignType: CampaignType
    ): Promise<ApolloQueryResult<DefaultCampaignsEditQuery>> => {
        return this.createQuery<DefaultCampaignsEditQuery>(defaultCampaignsEditQuery, {
            variables: {
                login: login,
                campaignTypes: [campaignType]
            }
        });
    };

    public getGoalsAvailableForKeyGoals = (
        login: string,
        availableForKeyGoalsInput: CampaignIdAndMetrikaCountersInput[]
    ): Promise<ApolloQueryResult<CampaignsAvailableForKeyGoalsQuery>> => {
        return this.createQuery<CampaignsAvailableForKeyGoalsQuery>(campaignsAvailableForKeyGoalsQuery, {
            variables: {
                login: login,
                availableForKeyGoalsInput: {
                    meaningfulGoalContainers: availableForKeyGoalsInput
                }
            }
        });
    };

    public getGoalsAvailableForKeyGoalsForNewCampaign = (
        login: string,
        counterIdsIn: string[]
    ): Promise<ApolloQueryResult<CampaignsAvailableForKeyGoalsForNewCampaignQuery>> => {
        return this.createQuery<CampaignsAvailableForKeyGoalsForNewCampaignQuery>(
            campaignsAvailableForKeyGoalsForNewCampaignQuery,
            {
                variables: {
                    login: login,
                    availableForKeyGoalsInput: counterIdsIn
                }
            }
        );
    };

    public getCampaignsABSegments = (
        login: string,
        abSegmentsInput: CampaignIdAndMetrikaCountersInput[]
    ): Promise<ApolloQueryResult<CampaignsABSegmentsQuery>> => {
        return this.createQuery<CampaignsABSegmentsQuery>(campaignsABSegmentsQuery, {
            variables: {
                login: login,
                abSegmentsInput: {
                    meaningfulGoalContainers: abSegmentsInput
                }
            }
        });
    };

    public getCampaignsABSegmentsForNewCampaign = (
        login: string,
        counterIdsIn: string[]
    ): Promise<ApolloQueryResult<CampaignsABSegmentsQueryForNewCampaign>> => {
        return this.createQuery<CampaignsABSegmentsQueryForNewCampaign>(campaignsABSegmentsQueryForNewCampaign, {
            variables: {
                login: login,
                abSegmentsInput: counterIdsIn
            }
        });
    };

    public getTimeZoneGroups = (login: string): Promise<ApolloQueryResult<TimeZoneGroupsQuery>> => {
        return this.createQuery<TimeZoneGroupsQuery>(timeZoneGroupsQuery);
    };
}
